import * as turf from '@turf/turf';

/**
 * "MM/DD HH:mm" 形式の文字列をYYYYMMDDhhmmss（UTC基準）に変換する
 * @param {String} date -"MM/DD HH:mm" 形式の文字列
 * @returns {String} YYYYMMDDhhmmss（UTC基準）
 */
export const getStrDateTimePath = (date) => {

  // 現在の年を取得（動的に）
  const year = new Date().getFullYear();

  // JSTとして認識されるように、年を付加しタイムゾーンも指定
  const jstDate = new Date(`${year}/${date}:00 +0900`);

  // UTCに変換して各パーツを取得
  const yyyy = jstDate.getUTCFullYear();
  const mm = String(jstDate.getUTCMonth() + 1).padStart(2, '0'); // 月は0始まり
  const dd = String(jstDate.getUTCDate()).padStart(2, '0');
  const hh = String(jstDate.getUTCHours()).padStart(2, '0');
  const mi = String(jstDate.getUTCMinutes()).padStart(2, '0');
  const ss = String(jstDate.getUTCSeconds()).padStart(2, '0');

  // フォーマット整形
  const utcString = `${yyyy}${mm}${dd}${hh}${mi}${ss}`;

  return utcString
}

const maxPerIndexWithSource = (matrix, ids) => {
  if (matrix.length === 0) return { max: [], src: [] };
  const len = Math.max(...matrix.map(row => row.length));

  const max = Array(len).fill(-Infinity);
  const src = Array(len).fill(null);

  for (let i = 0; i < len; i++) {
    for (let j = 0; j < matrix.length; j++) {
      const val = matrix[j][i];
      if (val !== undefined && val > max[i]) {
        max[i] = val;
        src[i] = ids[j]; // 最大値が出たIDを記録
      }
    }
  }

  return { max, src };
};

/**
 * 全日時断面のリスクに地域情報を付与する
 * @param {Object} features - 地域情報取得用のデータ
 * @param {Object} data - 全日時断面のリスクデータ
 * @returns {Array} 全日時断面のリスクと地域情報
 */
export const convertAreaDataFromMshDataByKeyName = (mshMasterFeatures, data) => {
  // メッシュのJSONをマスターとして字コードとメッシュコードの組み合わせで
  // [ {area_code: [msh_code01,msh_code02,msh_code03,msh_code04,msh_code05,msh_code06,msh_code07] }]
  // のような配列を作成する
  const areaCodeMap = {};
  for (const { properties: { id, area_code } } of mshMasterFeatures) {
    if (!areaCodeMap[area_code])
      areaCodeMap[area_code] = [];
    areaCodeMap[area_code].push(id);
  }

  // ② code → 値のマッピング作成（検索高速化）
  const valueMap = Object.fromEntries(data.map(v => [v.code, v]));

  // ③ area_code ごとに val_matrix の最大値を計算
  const result = Object.entries(areaCodeMap).map(([area_code, ids]) => {
    const val_matrix = [];
    const used_ids = [];
    for (const id of ids) {
      const entry = valueMap[id];
      if (entry) {
        val_matrix.push(entry.val);
        used_ids.push(id);
      }
    }

    const { max: val, src: src } = maxPerIndexWithSource(val_matrix, used_ids);
    return {
      code: Number(area_code),
      val,
      src,
    };
  });
  return result
}

/**
 * 予報断面の最大値と地域情報をランキングで取得する
 * @param {Object} areaFeatures - 字情報取得用のデータ
 * @param {Object} mshFeatures - メッシュ情報取得用のデータ
 * @param {Object} data - 全日時断面のデータ
 * @param {number} liimit - 上位何件まで取得するか
 * @returns {Array} 予報断面の最大値と地域情報
 */
export const getRankWithAreaInfo = (areaFeatures, mshFeatures, data, liimit) => {
  const offset = 12;

  const result_all = data.flatMap(item => {
    const area_code = item.code
    // 対象字コードの feature を抽出
    const filteredAreaFeatures = areaFeatures.find(f =>
      f.properties.id === area_code
    );
    // featureから字名を取得
    const area_name = filteredAreaFeatures.properties.name

    // 予報値の切り出し
    const sliced = item.val.slice(offset);
    // 予報値の最大値取得
    const max = Math.max(...sliced);
    // 予報値の最大値の断面に該当するメッシュコードを取得
    const indexInSliced = sliced.indexOf(max);
    const index_Val = indexInSliced + offset;
    const msh_code = item.src[index_Val]
    if (!msh_code || msh_code === null || msh_code === undefined)
      return []

    // 対象メッシュコードの feature を抽出
    const filteredMshFeatures = mshFeatures.find(f =>
      f.properties.id === msh_code
    );
    // メッシュfeatureから中心緯度経度を取得
    const lat_lng = turf.centroid(filteredMshFeatures).geometry.coordinates;

    const area_lng = lat_lng[0]
    const area_lat = lat_lng[1]

    return [{ area_code, area_name, msh_code, index_Val, area_lat, area_lng, max }];
  });
  const result = result_all
    .sort((a, b) => b.max - a.max)
    .slice(0, liimit);

  return result
};

/**
 * 予報断面の最大値と地域情報をランキングで取得する
 * @param {Object} areaFeatures - 字情報取得用のデータ
 * @param {Object} mshFeatures - メッシュ情報取得用のデータ
 * @param {Object} data - 全日時断面のデータ
 * @param {number} liimit - 上位何件まで取得するか
 * @returns {Array} 予報断面の最大値と地域情報
 */
export const getRankWithBsnInfo = (masterInfo, data, liimit) => {
  const offset = 6;

  const result_all = data.flatMap(item => {
    const bsn_code = item.code
    // 対象字コードの feature を抽出
    const filteredMasterInfo = masterInfo.find(info =>
      info.code === bsn_code
    );
    // featureから字名を取得
    const bsn_name = filteredMasterInfo.name

    // 予報値の切り出し
    const sliced = item.val.slice(offset);
    // 予報値の最大値取得
    const max = Math.max(...sliced);
    // 予報値の最大値の断面に該当するメッシュコードを取得
    const indexInSliced = sliced.indexOf(max);
    const index_Val = indexInSliced + offset;

    const bsn_lng = filteredMasterInfo.lng
    const bsn_lat = filteredMasterInfo.lat
    // TODO 既存の実装に合わせるために「area」としているが、要変更
    return [{
      area_code: bsn_code,
      area_name: bsn_name,
      index_Val: index_Val,
      area_lat: bsn_lat,
      area_lng: bsn_lng,
      max: max
    }];
  });
  const result = result_all
    .sort((a, b) => b.max - a.max)
    .slice(0, liimit);

  return result
};

/**
 * 河川情報を取得
 * @param {Array} code_ary - 選択河川
 * @param {Object} apiData - APIから取得したデータ
 * @param {Object} features - 河川名取得用のデータ
 * @returns {Array} 全日時断面のリスクと1時間雨量
 */
export const getBsnInfo = (code_ary, apiData, features) => {
  const new_ary = apiData.map(item => ({
    code: item.code,
    // 河川名を取得
    name: features.find(f =>
      Number(item.code) === f.properties.bsn_code)?.properties.txt_river_name_show ?? 'ー',
    lat: item.lat,
    lng: item.lng,
  }));

  // 必要なコードのもののみ抽出
  const result = new_ary.filter(item =>
    code_ary.includes(item.code)
  );

  return result
};

export const getFilteredDataByArea = (areas, apiData, keyName) => {
  // 欲しいキーのリストを用意
  let targetKeys = []
  for (const area of areas) {
    targetKeys.push(`${keyName}${area}`)
  }

  // 対象キーのみ抜き出して新しいオブジェクトを作成
  const filteredData = Object.fromEntries(
    Object.entries(apiData).filter(([key]) => targetKeys.includes(key))
  );
  return filteredData
};

const getMultiPolygonCenter = (coordinates) => {
  const allCoords = [];

  coordinates.map(polygon => {
    polygon.map(ring => {
      ring.map(point => {
        allCoords.push(point); // [lon, lat]
      });
    });
  });

  const lons = allCoords.map(coord => coord[0]);
  const lats = allCoords.map(coord => coord[1]);

  const avgLon = lons.reduce((sum, val) => sum + val, 0) / lons.length;
  const avgLat = lats.reduce((sum, val) => sum + val, 0) / lats.length;

  return [avgLon, avgLat]; // [経度, 緯度]
}

/**
 * featuresから指定緯度経度が属するコードを取得
 * @param {Object} features - 地域情報取得用のデータ
 * @param {Array} lng_lat - 緯度経度
 * @returns {Number} コード
 */
export const findContainingFeature = (features, lng_lat) => {
  const pt = turf.point(lng_lat);
  for (const feature of features) {
    if (turf.booleanPointInPolygon(pt, feature)) {
      return feature.properties.id;
    }
  }
  return null; // 内包している feature がない
}

/**
 * featuresから指定緯度経度に近いコードを取得
 * @param {Object} features - 地域情報取得用のデータ
 * @param {Array} lng_lat - 緯度経度
 * @param {Number} threshold - 近さの許容範囲（単位：度。約0.001 ≒ 約100m）
 * @returns {Number} コード
 */
export const findNearestLineFeature = (features, lng_lat, threshold = 0.001) => {
  const clickPoint = turf.point(lng_lat);

  let nearest = null;
  let minDist = Infinity;

  for (const feature of features) {
    let line;

    if (feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString') {
      line = feature;
    } else if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
      // PolygonをLineに変換
      line = turf.polygonToLine(feature);
    } else {
      continue; // 無視
    }

    const snapped = turf.nearestPointOnLine(line, clickPoint, { units: 'degrees' });
    const dist = snapped.properties.dist;

    if (dist < minDist && dist < threshold) {
      nearest = feature;
      minDist = dist;
    }
  }
  const code = nearest?.properties?.id ?? nearest?.properties?.bsn_code;
  return code ?? null;
}

/**
 * 3次メッシュに変換したデータを取得
 * @param {Array} data - 4次メッシュデータ
 * @param {String} keyName - 参照したいデータのキー（h1_val_：雨量、risk_val_：リスク）
 * @returns {Array} 3次メッシュデータ
 */
export const convert3dMshDataFrom4dMshData = (data, keyName) => {
  const seen = new Set();
  let map_data_for_3d_msh_ = [];

  for (const item of data) {
    // const groupCode = Math.floor(item.code / 10); // 上位8桁（例: 11111000）
    const groupCode = item.code;
    if (!seen.has(groupCode)) {
      seen.add(groupCode);
      map_data_for_3d_msh_.push({
        code: groupCode,
        val: item[keyName]
      });
    }
  }
  return map_data_for_3d_msh_;
}

/**
 * メッシュの警報基準データの取得
 * @param {String} targetId - 4次メッシュデータ
 * @param {String} mastreMshFeatures - 参照したいデータのキー（h1_val_：雨量、risk_val_：リスク）
 */
export const getSrRiskLevel = (targetId, mastreMshFeatures) => {
  const feature = mastreMshFeatures.find(f => f.properties.id === targetId);
  if (!feature) return null;

  const p = feature.properties;
  return {
    LEVEL_5: p.int_rain_ppl_std_from_sr ? parseInt(p.int_rain_ppl_std_from_sr) : null,
    LEVEL_4: p.soil_rainfall_index ? parseInt(p.soil_rainfall_index) : null,
    LEVEL_3: p.int_rain_red_std_from_sr ? parseInt(p.int_rain_red_std_from_sr) : null,
    LEVEL_2: p.int_rain_ylw_std_from_sr ? parseInt(p.int_rain_ylw_std_from_sr) : null,
  };
}